import React, { useState, useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ChevronDown, HelpCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';

gsap.registerPlugin(ScrollTrigger);

const FAQSection = () => {
    const [openIndex, setOpenIndex] = useState(null);
    const sectionRef = useRef(null);
    const { language } = useLanguage();
    const t = translations[language];

    useEffect(() => {
        // FAQ Schema for SEO
        const schema = {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": t.faq.items.map(item => ({
                "@type": "Question",
                "name": item.q,
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": item.a.replace(/\n/g, '<br/>')
                }
            }))
        };

        const script = document.createElement('script');
        script.id = 'faq-schema';
        script.type = 'application/ld+json';
        script.text = JSON.stringify(schema);

        // Remove old schema if exists
        const oldScript = document.getElementById('faq-schema');
        if (oldScript) {
            document.head.removeChild(oldScript);
        }

        document.head.appendChild(script);

        const ctx = gsap.context(() => {
            gsap.fromTo('.faq-item',
                { opacity: 0, x: -30 },
                {
                    opacity: 1,
                    x: 0,
                    duration: 0.6,
                    stagger: 0.1,
                    ease: 'power3.out',
                    scrollTrigger: {
                        trigger: sectionRef.current,
                        start: 'top 70%'
                    }
                }
            );
        }, sectionRef);

        return () => {
            ctx.revert();
            const s = document.getElementById('faq-schema');
            if (s) document.head.removeChild(s);
        };
    }, [language, t.faq.items]);

    const toggleFAQ = (index) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    return (
        <section id="faq" ref={sectionRef} className="py-20 lg:py-32 bg-gray-50 dark:bg-gray-800/50 transition-colors duration-300">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Section Header */}
                <div className="text-center mb-16">
                    <span className="inline-block bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 px-4 py-2 rounded-full text-sm font-semibold mb-4" style={{ fontFamily: "'Poppins', sans-serif" }}>
                        {t.faq.badge}
                    </span>
                    <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6" style={{ fontFamily: "'Poppins', sans-serif" }}>
                        {t.faq.title} <span className="text-red-600 dark:text-red-500">{t.faq.highlight}</span>
                    </h2>
                    <p className="text-xl text-gray-600 dark:text-gray-300" style={{ fontFamily: "'Poppins', sans-serif" }}>
                        {t.faq.subtitle}
                    </p>
                </div>

                {/* FAQ List */}
                <div className="space-y-4">
                    {t.faq.items.map((faq, index) => (
                        <div
                            key={index}
                            className="faq-item bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-700 overflow-hidden transition-all duration-300 hover:shadow-md"
                        >
                            <button
                                onClick={() => toggleFAQ(index)}
                                className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none"
                            >
                                <div className="flex items-center gap-4">
                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors duration-300 ${openIndex === index ? 'bg-red-500 text-white' : 'bg-red-50 text-red-500 dark:bg-red-900/20 dark:text-red-400'}`}>
                                        <HelpCircle className="w-5 h-5" />
                                    </div>
                                    <span className={`text-lg font-semibold transition-colors duration-300 ${openIndex === index ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}`} style={{ fontFamily: "'Poppins', sans-serif" }}>
                                        {faq.q}
                                    </span>
                                </div>
                                <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform duration-300 ${openIndex === index ? 'rotate-180' : ''}`} />
                            </button>

                            <div className={`transition-all duration-300 ease-in-out ${openIndex === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'} overflow-hidden`}>
                                <div className="px-6 pb-6 pt-0 ml-12">
                                    <div className="h-px w-full bg-gray-100 dark:bg-gray-800 mb-4"></div>
                                    <p className="text-gray-600 dark:text-gray-300 whitespace-pre-line leading-relaxed" style={{ fontFamily: "'Poppins', sans-serif" }}>
                                        {faq.a}
                                    </p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Bottom CTA */}
                <div className="mt-12 text-center">
                    <p className="text-gray-600 dark:text-gray-400" style={{ fontFamily: "'Poppins', sans-serif" }}>
                        {t.faq.cta.includes('Still have questions?') ? (
                            <>
                                Still have questions? <a href="#contact" className="text-red-600 dark:text-red-500 font-semibold hover:underline">Contact our support team</a>
                            </>
                        ) : (
                            <>
                                तरीही काही प्रश्न आहेत? <a href="#contact" className="text-red-600 dark:text-red-500 font-semibold hover:underline">आमच्या सपोर्ट टीमशी संपर्क साधा</a>
                            </>
                        )}
                    </p>
                </div>
            </div>
        </section>
    );
};

export default FAQSection;
